import { useState } from 'react';
import {
    X, Lock, Unlock, RefreshCw, Eye, Monitor, Gauge, ScanLine, Power, LogOut,
    MessageCircle, TerminalSquare, ShieldOff, Pencil, Trash2,
} from 'lucide-react';
import api from '../api/client';
import useAuthStore, { PERMISSIONS } from '../stores/authStore';
import ScreenMirrorModal from './ScreenMirrorModal';
import RemoteControlModal from './RemoteControlModal';
import BenchmarkModal from './BenchmarkModal';
import ScanAppsModal from './ScanAppsModal';
import ChatPanel from './ChatPanel';
import AdminConsoleModal from './AdminConsoleModal';
import { newRemoteControlClientId } from '@shared/remote-control/clientId.js';

/**
 * The full remote-action flow for one PC: command grid (lock/unlock/reboot/
 * shutdown/log-out), the four sub-modals (mirror/benchmark/scan-apps/admin
 * console), chat, and record management (rename/remove/console-toggle).
 *
 * Originally built inline in CafeDetail.jsx (one café's PC grid); extracted
 * so PCRegistry.jsx (the fleet-wide table) can offer the SAME actions
 * without a second, drifting copy. This is the cross-café-enabled version of
 * what café admins already have in primus-admin-main's PCManagement.jsx —
 * see internal_remote_commands.py / internal_pc_management.py for why the
 * café-scoped endpoints don't work for a SuperAdmin token.
 *
 * `pc` needs at least: id, name, status ("online"/"offline") or online
 * (bool), admin_console_enabled. `cafeId` is passed explicitly rather than
 * read off `pc` so a caller with its own café id in scope (CafeDetail's
 * route param) doesn't need it duplicated onto every pc object.
 * `onClose` — dismiss the WHOLE flow (used by the command grid's own X/
 * backdrop, and by Remove since there's no PC left to come back to). Every
 * individual action instead returns to the command grid when it's dismissed
 * (sub-modals clear their own `showX` flag; Lock/Reboot/etc. and Rename just
 * don't call onClose on success) — closing an action should land you back in
 * the menu, not kick you out of the whole flow.
 * `onChanged` — called after a rename/remove/console-toggle succeeds, so the
 * caller can refetch its own PC list; this component holds no list itself.
 */
export default function PCActionModal({ pc, cafeId, onClose, onChanged }) {
    const { hasPermission, isSuperAdmin } = useAuthStore();
    const canEdit = hasPermission(PERMISSIONS.EDIT_CAFE_DETAILS) || isSuperAdmin();
    const canRemoteAccess = hasPermission(PERMISSIONS.REMOTE_PC_ACCESS) || isSuperAdmin();
    const canExecuteCommands = hasPermission(PERMISSIONS.EXECUTE_PC_COMMANDS) || isSuperAdmin();

    const [dispatching, setDispatching] = useState(null);
    const [commandError, setCommandError] = useState(null);
    const [showMirror, setShowMirror] = useState(false);
    const [showRemoteControl, setShowRemoteControl] = useState(false);
    const [showBenchmark, setShowBenchmark] = useState(false);
    const [showScanApps, setShowScanApps] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [showAdminConsole, setShowAdminConsole] = useState(false);
    const [consoleEnabled, setConsoleEnabled] = useState(!!pc.admin_console_enabled);

    const online = pc.status === 'online' || pc.online === true;
    const showingSubModal = showMirror || showRemoteControl || showBenchmark || showScanApps || showChat || showAdminConsole;

    // Take Control needs its own pair of handlers rather than executeCommand,
    // which calls onClose() on success. That is right for a fire-and-forget
    // command like Lock and wrong here: this one has to OPEN a window and keep
    // it open. The pair also has to be symmetric — start_remote_control asks
    // the elevator to launch the agent, and stop_remote_control asks it to
    // stop, so skipping the stop leaves an agent running on a cafe machine.
    // (The lease would eventually end it by itself, within 30s of the browser
    // going quiet, but relying on the safety net for the ordinary path is how
    // you end up never noticing it has broken.)
    // One id per Take Control window, so a shared session can tell its windows apart.
    const [rcClientId, setRcClientId] = useState(null);

    const startRemoteControl = async () => {
        setCommandError(null);
        setDispatching('start_remote_control');
        const clientId = newRemoteControlClientId();
        try {
            await api.post(`/internal/pcs/${cafeId}/command`, {
                pc_id: pc.id,
                command: 'start_remote_control',
                params: JSON.stringify({ client_id: clientId }),
            });
            setRcClientId(clientId);
            setShowRemoteControl(true);
        } catch (err) {
            setCommandError(err.response?.data?.detail || 'Failed to start remote control.');
        } finally {
            setDispatching(null);
        }
    };

    const stopRemoteControl = () => {
        // Best effort, and deliberately not awaited: the window is closing
        // either way, and making the admin wait on a round-trip to dismiss it
        // would be worse than the agent lingering for one lease period.
        api.post(`/internal/pcs/${cafeId}/command`, {
            pc_id: pc.id,
            command: 'stop_remote_control',
            params: rcClientId ? JSON.stringify({ client_id: rcClientId }) : null,
        }).catch(() => { /* the lease ends the session regardless */ });
        // Return to the command grid rather than exiting the whole flow —
        // same reasoning as the other sub-modals below.
        setShowRemoteControl(false);
    };

    // Same contract as RemoteCommands.jsx / CafeDetail.jsx:
    // POST /internal/pcs/{cafe_id}/command { pc_id, command, params }.
    // Deliberately does NOT call onClose() on success — stays on the command
    // grid so firing Lock/Reboot/etc. doesn't also kick the operator out of
    // the whole flow. Only the backdrop/X and Remove close everything.
    const executeCommand = async (cmd, params = null) => {
        setCommandError(null);
        setDispatching(cmd);
        try {
            await api.post(`/internal/pcs/${cafeId}/command`, {
                pc_id: pc.id,
                command: cmd,
                params: params ? JSON.stringify(params) : null,
            });
        } catch (err) {
            setCommandError(err.response?.data?.detail || 'Failed to send command.');
        } finally {
            setDispatching(null);
        }
    };

    // PATCH /internal/pcs/{cafe_id}/{pc_id} -- rename and admin-console
    // toggle share this endpoint (internal_pc_management.py's update_cafe_pc,
    // ported from client_pc.py's update_pc). Stays on the command grid after
    // a successful rename, same reasoning as executeCommand above.
    const renamePC = async () => {
        const name = window.prompt('New name for this PC:', pc.name);
        if (!name || !name.trim() || name.trim() === pc.name) return;
        setCommandError(null);
        setDispatching('rename');
        try {
            await api.patch(`/internal/pcs/${cafeId}/${pc.id}`, { name: name.trim() });
            onChanged?.();
        } catch (err) {
            setCommandError(err.response?.data?.detail || 'Failed to rename PC.');
        } finally {
            setDispatching(null);
        }
    };

    const toggleConsole = async () => {
        setCommandError(null);
        setDispatching('console');
        try {
            await api.patch(`/internal/pcs/${cafeId}/${pc.id}`, {
                admin_console_enabled: !consoleEnabled,
            });
            setConsoleEnabled((v) => !v);
            onChanged?.();
        } catch (err) {
            setCommandError(err.response?.data?.detail || 'Failed to update PC.');
        } finally {
            setDispatching(null);
        }
    };

    // DELETE /internal/pcs/{cafe_id}/{pc_id} -- cascade delete, ported from
    // client_pc.py's delete_pc.
    const removePC = async () => {
        if (!window.confirm(`Remove ${pc.name} from this café's registry? This cannot be undone.`)) return;
        setCommandError(null);
        setDispatching('remove');
        try {
            await api.delete(`/internal/pcs/${cafeId}/${pc.id}`);
            onChanged?.();
            onClose();
        } catch (err) {
            setCommandError(err.response?.data?.detail || 'Failed to remove PC.');
        } finally {
            setDispatching(null);
        }
    };

    return (
        <>
            {!showingSubModal && (
                <div className="modal-backdrop" onClick={onClose}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal__header">
                            <h2 className="modal__title">Remote Access: {pc.name}</h2>
                            <button className="modal__close" onClick={onClose}><X size={20} /></button>
                        </div>
                        <div className="modal__body">
                            <p className="modal-info">Choose an action to execute on this PC.</p>
                            {!online && (
                                <p className="modal-error">This PC hasn&apos;t reported in recently — device actions below are disabled, but Rename/Remove/Console still work.</p>
                            )}
                            {commandError && <p className="modal-error">{commandError}</p>}
                            <div className="command-grid">
                                <button className="command-btn" onClick={() => executeCommand('lock')} disabled={!canExecuteCommands || !!dispatching || !online}>
                                    <Lock size={20} /><span>{dispatching === 'lock' ? 'Locking…' : 'Lock'}</span>
                                </button>
                                <button className="command-btn" onClick={() => executeCommand('unlock')} disabled={!canExecuteCommands || !!dispatching || !online}>
                                    <Unlock size={20} /><span>{dispatching === 'unlock' ? 'Unlocking…' : 'Unlock'}</span>
                                </button>
                                <button className="command-btn" onClick={() => executeCommand('restart')} disabled={!canExecuteCommands || !!dispatching || !online}>
                                    <RefreshCw size={20} /><span>{dispatching === 'restart' ? 'Rebooting…' : 'Reboot'}</span>
                                </button>
                                <button className="command-btn" onClick={() => setShowMirror(true)} disabled={!canRemoteAccess || !online} title={!online ? 'PC is offline — no screen to mirror.' : undefined}>
                                    <Eye size={20} /><span>Mirror</span>
                                </button>
                                {/*
                                  Take Control — requirement 5 of
                                  remote_control.md, which names superadmin
                                  explicitly and describes "the operator (from
                                  anywhere)" as the case the feature exists for.
                                  Gated on the same permission as Mirror, since
                                  driving a PC is strictly more than watching it.
                                */}
                                <button className="command-btn" onClick={startRemoteControl} disabled={!canRemoteAccess || !!dispatching || !online} title={!online ? 'PC is offline — nothing to control.' : 'Take control — see and drive this PC'}>
                                    <Monitor size={20} /><span>{dispatching === 'start_remote_control' ? 'Starting…' : 'Take Control'}</span>
                                </button>
                                <button className="command-btn" onClick={() => setShowBenchmark(true)} disabled={!canExecuteCommands || !online}>
                                    <Gauge size={20} /><span>Benchmark</span>
                                </button>
                                <button className="command-btn" onClick={() => setShowScanApps(true)} disabled={!canExecuteCommands || !online}>
                                    <ScanLine size={20} /><span>Scan apps</span>
                                </button>
                                <button className="command-btn" onClick={() => executeCommand('shutdown')} disabled={!canExecuteCommands || !!dispatching || !online}>
                                    <Power size={20} /><span>{dispatching === 'shutdown' ? 'Shutting down…' : 'Shutdown'}</span>
                                </button>
                                <button className="command-btn" onClick={() => executeCommand('logout')} disabled={!canExecuteCommands || !!dispatching || !online}>
                                    <LogOut size={20} /><span>{dispatching === 'logout' ? 'Logging out…' : 'Log out user'}</span>
                                </button>
                                <button className="command-btn" onClick={() => setShowChat(true)}>
                                    <MessageCircle size={20} /><span>Chat</span>
                                </button>
                                {consoleEnabled ? (
                                    <>
                                        <button className="command-btn" onClick={() => setShowAdminConsole(true)} disabled={!canExecuteCommands || !online}>
                                            <TerminalSquare size={20} /><span>Console</span>
                                        </button>
                                        <button className="command-btn" onClick={toggleConsole} disabled={!canExecuteCommands || !!dispatching}>
                                            <ShieldOff size={20} /><span>{dispatching === 'console' ? 'Updating…' : 'Disable console'}</span>
                                        </button>
                                    </>
                                ) : (
                                    <button className="command-btn" onClick={toggleConsole} disabled={!canExecuteCommands || !!dispatching}>
                                        <TerminalSquare size={20} /><span>{dispatching === 'console' ? 'Updating…' : 'Enable console'}</span>
                                    </button>
                                )}
                                <button className="command-btn" onClick={renamePC} disabled={!canEdit || !!dispatching}>
                                    <Pencil size={20} /><span>{dispatching === 'rename' ? 'Renaming…' : 'Rename'}</span>
                                </button>
                                <button className="command-btn command-btn--danger" onClick={removePC} disabled={!canEdit || !!dispatching}>
                                    <Trash2 size={20} /><span>{dispatching === 'remove' ? 'Removing…' : 'Remove'}</span>
                                </button>
                            </div>
                            <p className="modal-notice">All commands are logged for audit purposes.</p>
                        </div>
                    </div>
                </div>
            )}

            {showMirror && <ScreenMirrorModal pc={pc} cafeId={cafeId} onClose={() => setShowMirror(false)} />}
            {showRemoteControl && <RemoteControlModal pc={pc} cafeId={cafeId} clientId={rcClientId} onClose={stopRemoteControl} />}
            {showBenchmark && <BenchmarkModal pc={pc} cafeId={cafeId} onClose={() => setShowBenchmark(false)} />}
            {showScanApps && <ScanAppsModal pc={pc} cafeId={cafeId} onClose={() => setShowScanApps(false)} />}
            {showChat && <ChatPanel pc={pc} cafeId={cafeId} onClose={() => setShowChat(false)} />}
            {showAdminConsole && <AdminConsoleModal pc={pc} cafeId={cafeId} onClose={() => setShowAdminConsole(false)} />}

            <style>{`
                .modal-info { color: var(--text-secondary); font-size: var(--text-sm); margin-bottom: var(--space-6); }
                .modal-error {
                  margin-bottom: var(--space-4); padding: var(--space-3); border-radius: var(--radius-sm);
                  background: var(--status-danger-subtle); color: var(--status-danger); font-size: var(--text-sm);
                }
                .command-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-3); }
                .command-btn {
                  display: flex; flex-direction: column; align-items: center; gap: var(--space-2);
                  padding: var(--space-4); background: var(--glass-bg); border: 1px solid var(--glass-border);
                  border-radius: var(--radius-lg); color: var(--text-secondary); font-size: var(--text-sm);
                  cursor: pointer; transition: all var(--duration-fast) var(--ease-out);
                }
                .command-btn:hover:not(:disabled) { border-color: var(--accent-primary); color: var(--accent-primary); background: var(--accent-primary-subtle); }
                .command-btn:disabled { opacity: 0.5; cursor: not-allowed; }
                .command-btn--danger { color: var(--status-danger); }
                .command-btn--danger:hover:not(:disabled) { border-color: var(--status-danger); background: var(--status-danger-subtle); }
                @media (max-width: 480px) { .command-grid { grid-template-columns: repeat(2, 1fr); } }
                .modal-notice { margin-top: var(--space-6); font-size: var(--text-xs); color: var(--text-tertiary); text-align: center; }
            `}</style>
        </>
    );
}
